import { Text } from '@thanx/uikit/text'
import palette from 'constants/palette'
import React from 'react'
import type { StyleObject } from 'styletron-react'
import { useStyletron } from 'styletron-react'

// Generic tone-keyed pill for the RecoveryAI activation surfaces. See StatusBadge in
// this directory for the ActivityStatus-keyed variant — that one translates through
// `recovery_ai.activity.status` and is not interchangeable with this.
export type PillTone = 'ready' | 'attention' | 'recommended'

const TONE_BACKGROUND: Record<PillTone, string> = {
  ready: palette.nephritis10,
  attention: palette.eggYolk10,
  recommended: palette.nephritis20,
}

// All tones use grey90 text — the design's coloured labels all fail AA (see
// the component below). Exported for the unit test, same reason as
// `statusPillStyle`.
export const toneTextColor: Record<PillTone, string> = {
  ready: 'grey90',
  attention: 'grey90',
  recommended: 'grey90',
}

// Exported for the unit test — css() is a no-op under renderWithStore (no
// StyletronProvider), so style assertions have to read the object, not the DOM.
export const statusPillStyle = (tone: PillTone): StyleObject => ({
  alignSelf: 'flex-start',
  display: 'inline-block',
  backgroundColor: TONE_BACKGROUND[tone],
  // ~3px in the design — a rounded rectangle, NOT a capsule. Never 999px.
  borderRadius: '4px',
  // AI-887: the design pill is compact — tight vertical padding on the 10px
  // BodyText6 label (see below). Keep horizontal padding at 8px.
  padding: '2px 8px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
})

type Props = {
  tone: PillTone
  children: React.ReactNode
}

// All tones use grey90 text, a deliberate deviation from the design's coloured
// labels — every design pairing fails AA: nephritis-on-nephritis10 2.58:1,
// eggYolk50-on-eggYolk10 1.69:1, nephritis-on-nephritis20 2.47:1. grey90 clears
// AA comfortably on all three backgrounds (same call as StanceCard and
// OrderSummaryStrip) while keeping the brighter nephritis20 background product
// asked for on the "recommended" pill.
const StatusPill = ({ tone, children }: Props) => {
  const [css] = useStyletron()
  return (
    <div className={css(statusPillStyle(tone))}>
      {/* AI-887: BodyText6 (10px) matches the compact design pill; BodyText5
          (12px) rendered too large. Still normal-size text, so the AA 4.5:1
          rule the grey90 colour clears is unchanged. */}
      <Text.BodyText6 bold color={toneTextColor[tone]}>
        {children}
      </Text.BodyText6>
    </div>
  )
}

export default StatusPill
